import { action, observable } from "mobx";
import { FormulaFormVM } from "../template/FormulaFormVM";
import MessageCenter from "cd-eft/components/common/MessageCenter/MessageCenterVM";

interface ICubeForm {
  side: string,
  diagonal: string,
}
interface ICubeResults {
  Volume?: string;
  Area?: string;
}

export class CubeVM extends FormulaFormVM {
  readonly formLabelsForExport;
  readonly resultLabelsForExport;
  constructor(props) {
    super(props);
    // Any Form specific contructor code. Otherwise, remove contructor - use parent.
    this.formLabelsForExport = {
      side: "Side 'a'",
      diagonal: 'Diagonal',
    }
    this.resultLabelsForExport = {
      Volume: 'Volume (cu units)',
      Area: 'Area (sq units)',
    }
  }
  // Expand with onChange handlers, onBlur validation, Submit handler, on submit validation, individual variables, etc.
  @observable cubeForm: ICubeForm = {
    side: this.defaultValues?.side,
    diagonal: this.defaultValues?.diagonal,
  }
  @observable cubeResults: ICubeResults = {
    Volume: '0',
    Area: "0"
  }
  @observable cubeFormInputSnapshot: ICubeForm | undefined = undefined
  @observable hasResults: boolean = false;
  @observable cubeControl: number = 1
  @observable formValidation = {
    hasError: true,
    side: false,
    diagonal: false,
    formErrors: {
      side: '',
      diagonal: '',
    },
  };

  @action.bound handleOnKeyDown(event) {
    ['e', 'E', '+', '-'].includes(event.key) && event.preventDefault();
  }

  @action.bound CubeErrorClass(field) {
    const errorMessage = this.formValidation.formErrors[field];
    return errorMessage?.length === 0 ? '' : ' usa-input--error';
  }
  get diagonalControl(): boolean {
    return this.cubeControl === 0;
  }
  get sideControl(): boolean {
    return this.cubeControl === 1;
  }
  @action.bound setFixedValue(name, fixed) {
    const event1 = {
     target: {
     name: name,
     value: this.roundingHelperString(fixed, 2)
     }
     }
    this.handleFormChange(event1, true);
     }
  @action.bound async handleFormChange(event, within?:boolean) {
    const { name, value } = event.target;
    if (!within && value.length > 7) {
      return
    }
    switch (name) {
      case 'side':
        this.cubeForm.side = value;
        break;
      case 'diagonal':
        this.cubeForm.diagonal = value;
        break;
      case 'diagonalControl':
        this.cubeControl = 0
        break;
      case 'sideControl':
        this.cubeControl = 1
        break;
    }
  }
  @action.bound async validateInput(name, value) {
    const val = this.roundingHelperNumber(parseFloat(value), 2);
    if (value !== '' && value !== undefined) this.setFixedValue(name, val);

    switch (name) {
      case 'side':
        if (value === '' || val <= 0 || Number.isNaN(value)) {
          this.formValidation.side = true;
          this.formValidation.formErrors.side = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.side);
        }
        else {
          this.formValidation.side = false;
          this.formValidation.formErrors.side = '';
          this.formValidation.hasError = false;
          this.cubeForm.diagonal = (this.roundingHelperString((value * Math.sqrt(3)), 2))
        }
        break;
      case 'diagonal':
        if (value === '' || val <= 0 || Number.isNaN(value)) {
          this.formValidation.diagonal = true;
          this.formValidation.formErrors.diagonal = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.diagonal);
        }
        else {
          this.formValidation.diagonal = false;
          this.formValidation.formErrors.diagonal = '';
          this.formValidation.hasError = false;
          this.cubeForm.side = (this.roundingHelperString((value / Math.sqrt(3)), 2))
        }
        break;
    }
  }

  get validateRequiredInputs() {
    if (!this.formValidation.side && !this.formValidation.diagonal) {
      return true
    }
    return false
  }

  async validateReqInput() {
    await this.validateInput("side", this.cubeForm.side);
    await this.validateInput("diagonal", this.cubeForm.diagonal);
  }
  @action.bound  async computeCube() {
    this.disableComputeBtn = true;
    this.disablePrintBtn = true;
    this.isLoading = true;
    MessageCenter.blockCalls(['Common_26']);
    await this.validateReqInput();
    if (this.validateRequiredInputs) {
      this.disablePrintBtn = false;
      this.hasResults = true;
      this.isLoading = false;
      if (this.cubeControl === 1) {
        this.cubeResults = {
          Volume: this.roundingHelperString((Math.pow(parseFloat(this.cubeForm.side), 3)), 2),
          Area: this.roundingHelperString((6 * Math.pow(parseFloat(this.cubeForm.side), 2)), 2)
        };
      }
      else {
        this.cubeResults = {
          Volume: this.roundingHelperString((Math.pow((parseFloat(this.cubeForm.diagonal) / Math.sqrt(3)), 3)), 2),
          Area: this.roundingHelperString((6 * Math.pow((parseFloat(this.cubeForm.diagonal) / Math.sqrt(3)), 2)), 2)
        };
      }
      this.cubeFormInputSnapshot = { ...this.cubeForm };
    } else {
      await MessageCenter.addNotificationFromCode('Common_26');
      this.disablePrintBtn = true;
      this.hasResults = false;
    }
    MessageCenter.unblockCalls();
    this.isLoading = false;
    this.disableComputeBtn = false;
  }

  @action.bound launchAndExport() {
    this.disablePrintBtn = true;
    this.exportedInputs = [];
    this.exportedResults = [];

    for (const input in this.cubeFormInputSnapshot) {
      this.exportedInputs.push({
        label: this.formLabelsForExport[input],
        value: this.cubeFormInputSnapshot[input]
      })
    }
    for (const result in this.cubeResults) {
      const label = this.resultLabelsForExport[result];
      const value = this.cubeResults[result];
      if (label && value !== undefined) {
        this.exportedResults.push({
          label: this.resultLabelsForExport[result],
          value: this.cubeResults[result]
        })
      }
    }
    this.printFormula();
    this.disablePrintBtn = false;
  }

  @action.bound initializeDefaultValues(): void {
    this.cubeForm = {
      side: this.defaultValues.sideA !== undefined ? this.roundingHelperString(this.defaultValues.sideA, 2) : "0.00",
      diagonal: this.defaultValues.diagonal !== undefined ? this.roundingHelperString(this.defaultValues.diagonal, 2) : "0.00",
    }
    this.cubeFormInputSnapshot = { ...this.cubeForm }
  }
}